
import './MaualContent.scss';
import React, { useState, useEffect } from 'react';
import { FiDownload } from 'react-icons/fi';
import { IoClose } from 'react-icons/io5';
import DOMPurify from 'dompurify';
import fnc from '../mymod/commFunction';

const MaualContent = ({ selectedContent }) => {
    const [content, setContent] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [selectedImage, setSelectedImage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    const sanitizeContentHtml = (html) => {
        if (!html) return '';

        return DOMPurify.sanitize(html, {
            USE_PROFILES: { html: true },
            ADD_ATTR: ['target', 'rel']
        });
    };

    const isS3SignedUrl = (src) => {
        if (!src) return false;
        return /[?&]X-Amz-Algorithm=/i.test(src) || /[?&]X-Amz-Signature=/i.test(src);
    };

    const isLikelyS3Key = (src) => {
        if (!src) return false;
        const trimmed = String(src).trim();
        if (!trimmed) return false;
        if (/^(data:|blob:|mailto:|tel:|javascript:)/i.test(trimmed)) return false;
        if (/^(https?:)?\/\//i.test(trimmed)) return false;
        const normalized = trimmed.replace(/^\/+/, '');
        return /^(mathtailer\/|web\/|public\/)/i.test(normalized);
    };

    const extractS3KeyFromSrc = (src) => {
        if (!src) return null;
        const trimmed = String(src).trim();
        if (!trimmed) return null;

        if (isLikelyS3Key(trimmed)) {
            return trimmed.replace(/^\/+/, '');
        }

        try {
            const url = new URL(trimmed, window.location.origin);
            const pathname = decodeURIComponent((url.pathname || '').replace(/^\/+/, ''));
            if (!pathname) return null;

            if (/amazonaws\.com$/i.test(url.hostname) || /s3[.-]/i.test(url.hostname) || isS3SignedUrl(trimmed)) {
                return pathname;
            }
        } catch (error) {
            return null;
        }

        return null;
    };

    const requestSignedImageUrl = async (imagePath) => {
        const response = await fetch(`${fnc.svrUrl}/s3Routes/get-image-path`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ imagePath, sec: 600 })
        });

        if (!response.ok) return '';
        const data = await response.json();
        return data?.url || '';
    };

    const normalizeContentImageUrls = async (rawHtml) => {
        if (!rawHtml) return '';

        const parser = new DOMParser();
        const doc = parser.parseFromString(rawHtml, 'text/html');
        const imgNodes = Array.from(doc.querySelectorAll('img[src]'));

        if (imgNodes.length === 0) return rawHtml;

        const urlCache = new Map();

        await Promise.all(
            imgNodes.map(async (imgNode) => {
                const src = imgNode.getAttribute('src');
                const imagePath = extractS3KeyFromSrc(src);
                if (!imagePath) return;

                if (!urlCache.has(imagePath)) {
                    urlCache.set(imagePath, requestSignedImageUrl(imagePath));
                }

                const signedUrl = await urlCache.get(imagePath);
                if (signedUrl) {
                    imgNode.setAttribute('src', signedUrl);
                }
            })
        );

        return doc.body.innerHTML;
    };


    useEffect(() => {
        let cancelled = false;
        setHasError(false);
        
        if (selectedContent) {
            setIsLoading(true);
            const contentText = selectedContent.content || '';
            normalizeContentImageUrls(contentText)
                .then((normalizedContent) => {
                    if (!cancelled) {
                        setContent(sanitizeContentHtml(normalizedContent || contentText));
                    }
                })
                .catch(() => {
                    if (!cancelled) {
                        setContent(sanitizeContentHtml(contentText));
                    }
                });
            
            // 첨부파일 목록 조회
            if (selectedContent.unit2_code) {
                loadAttachments(selectedContent.unit2_code);
            } else {
                setIsLoading(false);
            }
        } else {
            setContent('');
            setAttachments([]);
            setIsLoading(false);
        }

        return () => {
            cancelled = true;
        };
    }, [selectedContent]);

    const handleImageClick = (e) => {
        const img = e.target.closest('img');
        if (img) setSelectedImage(img.src);
    };

    const loadAttachments = (boardCode) => {
        fnc.sendAxios('/boardRoutes/select_boardFile', { boardCode }, (res) => {
            try {
                if (res && Array.isArray(res)) {
                    setAttachments(res);
                } else {
                    setAttachments([]);
                }
            } catch (error) {
                setAttachments([]);
                setHasError(true);
            } finally {
                setIsLoading(false);
            }
        });
    };

    const handleDownload = async (attachment) => {
        try {
            const response = await fetch(`${fnc.svrUrl}/s3Routes/get-image-path-rename`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    imagePath: attachment.fileName,
                    fileName: attachment.fileName2 || attachment.fileName.split('/').pop(),
                    sec: 600
                })
            });

            if (response.ok) {
                const data = await response.json();
                const link = document.createElement('a');
                link.href = data.url;
                link.download = attachment.fileName2 || attachment.fileName.split('/').pop();
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                alert('파일 다운로드에 실패했습니다.');
            }
        } catch (error) {
            alert('파일 다운로드 중 오류가 발생했습니다.');
        }
    };


    

    if (!selectedContent) {
        return (
            <div className='MaualContent'>
                <div className="empty-state">
                    <p>좌측 목록에서 항목을 선택해주세요.</p>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className='MaualContent'>
                <div className="empty-state">
                    <div className="loading-spinner"></div>
                    <p>내용을 불러오는 중입니다...</p>
                </div>
            </div>
        );
    }

    if (hasError) {
        return (
            <div className='MaualContent'>
                <div className="empty-state error-state">
                    <p>콘텐츠를 불러오는 중 오류가 발생했습니다.</p>
                    <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                        페이지를 새로고침하거나 다른 항목을 선택해보세요.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className='MaualContent'>
            <div className="header">
                <h2>{selectedContent.unit2_name}</h2>
            </div>
            <article className='art-jodit' onClick={handleImageClick}>
                {content ? (
                    <div
                        className="jodit-wysiwyg"
                        dangerouslySetInnerHTML={{ __html: content }}
                    />
                ) : (
                    <div className="no-content">
                        <p>내용이 없습니다.</p>
                        {selectedContent && (
                            <p className="item-info">
                                항목: {selectedContent.unit2_name}
                            </p>
                        )}
                    </div>
                )}
            </article>
            
            {attachments.length > 0 && (
                <div className="attachments-section">
                    <h3 className="attachments-title">첨부파일</h3>
                    <ul className="attachments-list">
                        {attachments.map((attachment) => (
                            <li key={attachment.sno} className="attachment-item">
                                <div className="attachment-info">
                                    <span className="file-name">{attachment.fileName2 || attachment.fileName.split('/').pop()}</span>
                                </div>
                                <button 
                                    className="btn-download"
                                    onClick={() => handleDownload(attachment)}
                                    title="다운로드"
                                >
                                    <FiDownload />
                                    다운로드
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* 이미지 확대 모달 */}
            {selectedImage && (
                <div className="image-modal" onClick={() => setSelectedImage(null)}>
                    <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="image-modal-close" onClick={() => setSelectedImage(null)}>
                            <IoClose />
                        </button>
                        <img src={selectedImage} alt="확대 이미지" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default MaualContent;